import React, {
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useState,
} from "react";
import { useServicesChecklists } from "../../hooks/useServicesChecklists";
import CountryContext from "../../CountryContext";
import { initialState, reducer } from "../../pages/WorksitesTasks/reducer";
import { useHistory } from "react-router-dom";
import { PAGINATION_TAKE } from "../../pages/WorksitesTasks/constants";
import { NotificationsContext } from "../../ui-components/Notifications";
import UserNotificationsContext from "../../UserNotificationsContext";
import { FormattedMessage, useIntl } from "react-intl";
import useCountryAxiosInstance from "../../hooks/useCountryAxiosInstance";
// import NotFound from "../../ui-components/pages/NotFound";
import { PageSection } from "../../ui-components/Container";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import Input from "../../ui-components/Input";
import SearchSelect from "../../ui-components/SearchSelect";
import { SparklesIcon } from "@heroicons/react/24/solid";
import TaskDetail from "../../pages/WorksitesTasks/TaskDetail";
import Placeholder from "../../ui-components/Placeholder";
import TaskListingFilters from "./TaskListingFilters";
import CreateTaskButton from "./TaskCreateButton";
import ListPlaceholder from "../ListPlaceholder";
import TaskListing from "./TaskListing";
import { Pagination } from "../Pagination";
import CloseIcon from "../CloseIcon";
import _ from "lodash";


const TaskSection = ({ worksite, task_id, activity_id, note_id, refreshStats }) => {
  // contexts
  const intl = useIntl();
  const history = useHistory();
  const { push } = useContext(NotificationsContext);
  const { country } = useContext(CountryContext);
  const { userNotifications, setUserNotifications } = useContext(
    UserNotificationsContext
  );

  // hooks
  const [state, dispatch] = useReducer(
    reducer,
    initialState(country, history, worksite, task_id)
  );
  const countryApiRequest = useCountryAxiosInstance(country.code);
  const [checklists] = useServicesChecklists(worksite.id, null, state?.tasks);

  // state
  const [templatesSearch, setTemplatesSearch] = useState("");
  const [selectedTemplateService, setSelectedTemplateService] = useState({
    label: intl.formatMessage({ id: "configurator" }),
    value: "",
  });

  // utilities
  const templates = checklists?.some((c) => c.surveys.length > 0)
    ? checklists
        .filter((c) => c.surveys.length > 0)
        .reduce(
          (acc, curr) => [...acc, ...curr.surveys.map((s) => ({
            ..._.omit(curr, ['surveys']),
            survey: s,
          }))],
          []
        )
        .sort((a, b) => a.created_at > b.created_at ? -1 : 1)
    : checklists?.map((c) => _.omit(c, 'surveys'));
  const templatesServicesOptions = Object.values(
    templates
      ?.filter((t) => !!t.id_service)
      ?.reduce(
        (prev, curr) => ({
          ...prev,
          [curr.id_service]: {
            label: curr.service.name,
            value: curr.id_service,
          },
        }),
        {}
      ) ?? {}
  );
  const visibleTemplates = templates?.filter((t) => {
    // check selected service, if any
    if (
      !!selectedTemplateService?.value &&
      t.service?.id !== selectedTemplateService.value
    )
      return false;
    // check search text, if any
    if (
      !!templatesSearch &&
      ![t.des_title, t.des_description].some((x) =>
        x.toLowerCase().includes(templatesSearch.toLowerCase())
      )
    )
      return false;
    return true;
  });

  // callbacks
  const fetchWorksitesTasks = useCallback(
    async (worksite_id, filters, paginationSkip) => {
      try {
        dispatch({ type: "loading", entity: "tasks" });
        const result = await countryApiRequest.get(
          "/worksites_tasks/all_paginated",
          {
            params: {
              id_worksite: worksite_id,
              skip: paginationSkip,
              limit: PAGINATION_TAKE,
              ...(filters.taskStatus && { status: filters.taskStatus }),
              ...(filters.sortMode && { [filters.sortMode]: "modified_at" }),
            },
          }
        );
        dispatch({
          type: "loaded_tasks",
          tasks: result.items,
          totalPages: parseInt(Math.ceil(result.total / PAGINATION_TAKE)),
        });
      } catch (error) {
        dispatch({ type: "loading_error" });

        push({
          type: "error",
          title: intl.formatMessage({ id: "an_error_occurred" }),
        });
        throw error;
      }
    },
    [countryApiRequest, push] // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    // noinspection JSIgnoredPromiseFromCall
    fetchWorksitesTasks(worksite.id, state.filters, state.paginationSkip);
  }, [fetchWorksitesTasks, worksite.id, state.filters, state.paginationSkip]);

  useEffect(() => {
    if (!userNotifications) {
      return;
    }
    // Delete notifications for the given task suggestions
    const notificationsToRemove = userNotifications
      ?.filter(
        (n) =>
          n.val_type === "worksite_task_suggestion" &&
          n.cod_worksite === parseInt(worksite.id)
      )
      ?.map((n) => n.id);
    if (notificationsToRemove.length) {
      setUserNotifications((prev) =>
        prev.filter((n) => !notificationsToRemove.includes(n.id))
      );
    }
  }, [worksite, state.tasks, setUserNotifications, userNotifications]);

  // ---------- RENDER
  if (state.error_load) {
    return <></>;
    // return (
    //   <div className="h-screen flex items-center justify-center">
    //     <NotFound />
    //   </div>
    // );
  }

  return (
    <>
      <PageSection
        title={intl.formatMessage({ id: "task_templates" })}
        description={intl.formatMessage({ id: "task_templates_helper" })}
      >
        <div className="w-full flex flex-col md:flex-row md:items-center gap-x-3 gap-y-1 mb-2">
          <div className="flex items-center gap-3 w-full md:mb-3">
            <MagnifyingGlassIcon className="w-5 mt-2" />
            <div className="flex-1 pt-1.5">
              <Input
                value={templatesSearch}
                onChange={(e) => {
                  setTemplatesSearch(e.target.value);
                }}
                placeholder={intl.formatMessage({
                  id: "search_task_template",
                })}
                hideBottomMargin={true}
              />
            </div>
          </div>
          <SearchSelect
            options={[
              {
                label: intl.formatMessage({ id: "configurator" }),
                value: "",
              },
              ...(templatesServicesOptions ?? []),
            ]}
            value={selectedTemplateService}
            onChange={setSelectedTemplateService}
            isMulti={false}
          />
        </div>
        <div className="relative">
          <div className="flex gap-3 overflow-x-auto pb-6 pr-6">
            {!visibleTemplates &&
              Array(3)
                .fill(null)
                .map((_, idx) => (
                  <Placeholder
                    key={idx}
                    classNames="w-72 h-40 rounded-lg"
                  />
                ))}
            {visibleTemplates?.length === 0 && (
              <div className="w-full h-40 flex items-center justify-center text-gray-400 text-lg">
                <FormattedMessage
                  id="no_available_tasks"
                  defaultMessage="Nessun task disponibile"
                />
              </div>
            )}
            {visibleTemplates?.map((c) => {
              const subtitleTooltipText = !!c.survey
                ? c.survey?.name || intl.formatMessage({id: "unnamed_configuration"})
                : c.des_description;
              const subtitle = !!c.survey
                ? <>
                    <span className="font-bold"><FormattedMessage id="configuration" /></span>:&nbsp;
                    {subtitleTooltipText}
                  </>
                : c.des_description;

              return (
                <div
                  key={c.id}
                  onClick={() => {
                    dispatch({
                      type: "open_task_modal",
                      open: true,
                      templateId: c.id,
                      surveyId: !c.survey ? null : c.survey.id,
                    });
                  }}
                  className="rounded-lg bg-kerakoll-700 shadow-lg border border-gray-200 text-white p-4 shadow hover:shadow-xl transition-all cursor-pointer group w-72 max-h-54 flex flex-col justify-between gap-4 flex-shrink-0"
                >
                  <div className="flex gap-1.5 text-xs font-medium">
                    <SparklesIcon className="w-3.5" />
                    <FormattedMessage
                      id="suggested_task"
                      defaultMessage="Task suggerito"
                    />
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <div
                      className="text-base font-semibold mb-0.5 leading-tight line-clamp-4"
                      title={c.des_title}
                    >
                      {c.des_title}
                    </div>
                    <div
                      className="text-xs text-ellipsis truncate"
                      title={subtitleTooltipText}
                    >
                      {subtitle}
                    </div>
                  </div>
                  <div className="flex">
                    <div className="bg-white p-2 px-3 rounded-lg text-kerakoll-700 font-bold grid place-content-center text-xs tracking-wide">
                      # {c.service.name}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
          {visibleTemplates?.length > 0 && (
            <div className="h-full w-8 from-transparent to-white bg-gradient-to-r absolute right-0 top-0" />
          )}
        </div>
      </PageSection>

      <PageSection
        title={intl.formatMessage({ id: "assigned_tasks" })}
        description={intl.formatMessage({ id: "assigned_tasks_helper" })}
      >
        <div className="w-full mb-2 flex flex-col md:flex-row flex-wrap gap-x-3 justify-end mb-6 md:mb-3">
          <TaskListingFilters
            filters={state.filters}
            setFilters={(filters) =>
              dispatch({ type: "setfilters", filters })
            }
          />
          {state.tasks.length > 0 && (
            <CreateTaskButton
              full
              onClick={() =>
                dispatch({ type: "open_task_modal", open: true })
              }
            />
          )}
        </div>

        {!state.loading_tasks && !state.tasks.length && (
          <div className="flex gap-8 justify-center py-10">
            <CreateTaskButton
              onClick={() =>
                dispatch({ type: "open_task_modal", open: true })
              }
            />
          </div>
        )}

        {
          // avoid flashing placeholder
          // when saving tasks
          !state.tasks.length && state.loading_tasks && <ListPlaceholder />
        }

        {state.tasks.length > 0 && (
          <>
            <TaskListing
              tasks={state.tasks}
              worksite={worksite}
              onItemSelectModify={async (id) => {
                dispatch({ type: "modify_task", id: id });
              }}
            />

            {state.totalPages > 1 && (
              <div className="my-20">
                <Pagination
                  onUpdate={(p) =>
                    dispatch({ type: "change_page", page: p })
                  }
                  currentPage={parseInt(
                    state.paginationSkip / PAGINATION_TAKE
                  )}
                  maxPages={state.totalPages}
                />
              </div>
            )}
          </>
        )}
      </PageSection>
      {state.taskCreateModalOpen !== false && (
        <>
          <div className="fixed top-0 left-0 w-full h-full z-10 bg-black opacity-50"></div>
          <div
            className={`animate-slideIn w-full sm:w-2/3 md:w-1/2 xl:w-1/3 fixed z-30 inset-y-0 right-0 bg-white overflow-y-auto shadow-2xl flex flex-col h-screen`}
          >
            <div className="px-5 flex py-5">
              <h1 className="text-sm font-extrabold leading-7 text-black-500 sm:text-xl whitespace-nowrap flex gap-4 items-center">
                {task_id
                  ? intl.formatMessage({ id: "worksite_task_update_title" })
                  : intl.formatMessage({ id: "worksite_task_create_title" })}
              </h1>

              <CloseIcon
                onClick={() => {
                  dispatch({
                    type: "open_task_modal",
                    open: false,
                    history: task_id !== undefined,
                  });
                }}
                text={intl.formatMessage({ id: "close" })}
              />
            </div>

            {state.taskCreateModalOpen && (
              <TaskDetail
                worksite={worksite}
                task_id={state.templateId ? undefined : task_id}
                activity_id={activity_id}
                note_id={note_id}
                template_id={state.templateId}
                survey_id={state.surveyId}
                onClose={() => {
                  dispatch({
                    type: "open_task_modal",
                    open: false,
                    history: task_id !== undefined,
                  });
                }}
                onSubmit={(close = true) => {
                  fetchWorksitesTasks(
                    worksite.id,
                    state.filters,
                    state.paginationSkip
                  );
                  refreshStats();
                  if (close) {
                    dispatch({ type: "task_created" });
                  }
                }}
              />
            )}
          </div>
        </>
      )}
    </>
  );
};

export default TaskSection;